import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router";
import {
  disregardDeal,
  executeDeal,
  renewDeal,
  reopenDeal,
  saveDeal,
  subscribeDeal,
  unsubscribeDeal,
  updateAssets,
  updateDealPointPerson,
  updateDealShareStatus,
  updateDealStage,
} from "actions/deal";
import { openModal } from "actions/modals";
import { push } from "connected-react-router";
import orderBy from "lodash/orderBy";
import moment from "moment";
import PropTypes from "prop-types";
import Subscribe from "rialto-icons/Subscribe";
import { proposalShape } from "shapes";
import userShape from "shapes/current_user";

import Action from "components/Action";
import Card, { CardBlock, CardTitle } from "components/Card";
import Collaborators from "components/Collaborators";
import DisregardModal from "components/DisregardModal";
import DealName from "components/EditableTitle";
import ExecuteModal from "components/ExecuteModal";
import FeePotential from "components/FeePotential";
import ShareStatusSelector from "components/ShareStatusSelector";
import StatusButton from "components/StatusButton";
import UsersDropdown from "components/UsersDropdown";
import { allDisabled, customDisabled } from "utils/deals/shareStatus";
import Restricted from "utils/Restricted";
import Translate from "utils/translate";

import DealHeaderActions from "../DealHeaderActions";
import { DisregardedDealMessage, ExecutedDealMessage } from "../DealStatusMessage";

import { DealStages } from "./DealStages";
import { DealAssets, ShareStatusModal } from ".";

import styles from "./DealHeader.css";

const { t } = new Translate("EnquiryHeader");

export class DealHeader extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      disregardModalOpen: false,
      executeModalOpen: false,
      shareStatusModalOpen: false,
    };

    this.handleCloseShareModal = this.handleCloseShareModal.bind(this);
    this.handleCustomShareStatus = this.handleCustomShareStatus.bind(this);
    this.handleSelectShareStatus = this.handleSelectShareStatus.bind(this);
  }

  componentWillReceiveProps() {
    this.setState({
      disregardModalOpen: false,
      executeModalOpen: false,
    });
  }

  handleCloseShareModal() {
    this.setState({ shareStatusModalOpen: false });
  }

  handleCustomShareStatus(brokers) {
    this.setState({ shareStatusModalOpen: false });
    this.props.onSelectShareStatus(
      "custom",
      brokers.map((b) => b.id)
    );
  }

  handleSelectShareStatus(selectedStatus) {
    const { deal, onSelectShareStatus } = this.props;
    const newStatus = selectedStatus || deal.shareStatus;

    if (!selectedStatus && newStatus !== "custom") return null;
    if (newStatus === "custom") this.setState({ shareStatusModalOpen: true });

    return onSelectShareStatus(newStatus);
  }

  render() {
    const {
      assets,
      currentUser,
      deal,
      dealStageAssets,
      isBroker,
      isCreator,
      isLoading,
      onCancel,
      onDisregard,
      onExecute,
      onRenew,
      onReopen,
      onSelectPointPerson,
      onSubscribe,
      onSwitchStage,
      onUnsubscribe,
      onUpdateAssets,
      onUpdateFeePotential,
      onUpdateName,
      proposals,
    } = this.props;

    const executedProposal = proposals.find((p) => p.id === deal.executedProposalId) || {};

    let statusBlock;
    if (deal.status === "dead") {
      statusBlock = <DisregardedDealMessage feedback={deal.disregardFeedback} />;
    } else if (deal.status === "executed") {
      statusBlock = (
        <ExecutedDealMessage
          breakDates={executedProposal.breakDates}
          commencementDate={executedProposal.commencementDate}
          executor={deal.executor}
          expirationDate={executedProposal.expirationDate}
          spaces={executedProposal.spaces}
        />
      );
    } else {
      statusBlock = (
        <>
          <DealStages
            deal={deal}
            dealStageAssets={dealStageAssets}
            onOpenDisregardModal={() => this.setState({ disregardModalOpen: true })}
            onOpenExecuteModal={() => this.setState({ executeModalOpen: true })}
            onSwitchStage={onSwitchStage}
          />
          <div className={styles.statusWrapper}>
            <div className={styles.statusInfo}>
              {t("status.days_active_from_now", { days: moment(deal.createdAt).fromNow(true) })}
            </div>
            <div className={styles.statusActions}>
              <StatusButton
                onClick={() => this.setState({ disregardModalOpen: true })}
                type="disregard"
              />
              <StatusButton
                onClick={() => this.setState({ executeModalOpen: true })}
                type="execute"
              />
            </div>
          </div>
        </>
      );
    }

    return (
      <div>
        {this.state.shareStatusModalOpen && (
          <ShareStatusModal
            assets={deal.assets}
            creator={deal.creator}
            isLoading={isLoading}
            onClose={this.handleCloseShareModal}
            onSave={this.handleCustomShareStatus}
            portfolioID={deal.portfolio.id}
            sharedBrokers={deal.sharedBrokers}
            isOpen
          />
        )}
        <DisregardModal
          isLoading={isLoading}
          isOpen={this.state.disregardModalOpen}
          onClose={() => this.setState({ disregardModalOpen: false })}
          onDisregard={onDisregard}
        />
        <ExecuteModal
          isOpen={this.state.executeModalOpen}
          onClose={() => this.setState({ executeModalOpen: false })}
          onExecute={onExecute}
          proposals={proposals}
          tenant={deal.name}
          type={deal.type}
        />
        <div className={styles.wrapper}>
          <Card className={styles.details}>
            <CardBlock>
              <CardTitle title={t("title")} />
            </CardBlock>
            <CardBlock>
              <DealName onUpdate={onUpdateName} title={deal.name || t("undisclosed")} />
              <Restricted requiredPermissions="assets">
                <DealAssets
                  assets={assets}
                  onUpdate={onUpdateAssets}
                  selectedAssets={deal.assets}
                />
              </Restricted>
              <FeePotential
                defaultCurrency={currentUser.company.currency}
                feePotential={deal.feePotential}
                onUpdate={onUpdateFeePotential}
              />
            </CardBlock>
            <CardBlock className={styles.dealActions}>
              <Restricted requiredPermissions="assets">
                <ShareStatusSelector
                  allDisabled={allDisabled(deal.assets)}
                  currentStatus={deal.shareStatus}
                  customDisabled={customDisabled(deal.assets)}
                  isBroker={isBroker}
                  isCreator={isCreator}
                  onSelect={this.handleSelectShareStatus}
                  sharedBrokerCount={deal.sharedBrokers.length}
                />
              </Restricted>
              <Action
                icon={Subscribe}
                isActive={deal.subscribed}
                onClick={deal.subscribed ? onUnsubscribe : onSubscribe}
                tooltip={deal.subscribed ? t("subscribed") : t("unsubscribed")}
              />
            </CardBlock>
          </Card>
          <Card className={styles.pointPerson}>
            <CardBlock>
              <CardTitle title={t("point_person.title")} />
            </CardBlock>
            <CardBlock>
              <UsersDropdown
                currentUser={currentUser}
                header={t("point_person.dropdown_header")}
                onSelect={onSelectPointPerson}
                selectedUser={deal.pointPerson}
                users={deal.collaborators}
              />
            </CardBlock>
            <CardBlock>
              <CardTitle title={t("collaborators.title")} />
            </CardBlock>
            <CardBlock>
              <Collaborators collaborators={deal.collaborators} />
            </CardBlock>
          </Card>
          <Card className={styles.status}>
            <CardBlock>
              {deal.status === "active" ? (
                <CardTitle title={t("status.title")} />
              ) : (
                <DealHeaderActions
                  isExecuted={deal.status === "executed"}
                  onCancel={onCancel}
                  onRenew={onRenew}
                  onReopen={onReopen}
                />
              )}
            </CardBlock>
            <CardBlock>{statusBlock}</CardBlock>
          </Card>
        </div>
      </div>
    );
  }
}

DealHeader.propTypes = {
  assets: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired,
    })
  ),
  currentUser: PropTypes.shape(userShape),
  deal: PropTypes.shape({
    name: PropTypes.string.isRequired,
    assets: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        name: PropTypes.string,
        status: PropTypes.oneOf(["public", "private", "invite"]),
      })
    ),
    creator: PropTypes.shape({
      id: PropTypes.number.isRequired,
      company: PropTypes.shape({
        id: PropTypes.number.isRequired,
      }),
    }),
    stage: PropTypes.string.isRequired,
    status: PropTypes.string.isRequired,
    shareStatus: PropTypes.string.isRequired,
    disregardFeedback: PropTypes.shape({
      actor: PropTypes.shape({
        id: PropTypes.number,
        fullName: PropTypes.string,
      }),
      reason: PropTypes.string,
      feedback: PropTypes.string,
      outcome: PropTypes.string,
    }),
    subscribed: PropTypes.bool,
    pointPerson: PropTypes.shape({
      fname: PropTypes.string,
      lname: PropTypes.string,
      id: PropTypes.number,
      deleted: PropTypes.bool,
      profilePictureUrl: PropTypes.string,
    }).isRequired,
    portfolio: PropTypes.shape({
      id: PropTypes.number.isRequired,
    }).isRequired,
    sharedBrokers: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        fname: PropTypes.string,
        lname: PropTypes.string,
        profilePictureUrl: PropTypes.string,
        title: PropTypes.string,
        company: PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      })
    ),
    spaces: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        name: PropTypes.string.isRequired,
        size: PropTypes.shape({
          value: PropTypes.number.isRequired,
          unit: PropTypes.string.isRequired,
        }).isRequired,
      })
    ),
    collaborators: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number,
        fname: PropTypes.string,
        lname: PropTypes.string,
        profilePictureUrl: PropTypes.string,
        title: PropTypes.string,
        company: PropTypes.shape({
          id: PropTypes.number,
          name: PropTypes.string,
        }),
      })
    ),
    createdAt: PropTypes.string,
  }),
  proposals: PropTypes.arrayOf(PropTypes.shape(proposalShape)),
  isBroker: PropTypes.bool,
  isCreator: PropTypes.bool,
  isLoading: PropTypes.bool,
  onCancel: PropTypes.func.isRequired,
  onDisregard: PropTypes.func.isRequired,
  onExecute: PropTypes.func.isRequired,
  onReopen: PropTypes.func.isRequired,
  onRenew: PropTypes.func.isRequired,
  onUpdateAssets: PropTypes.func.isRequired,
  onUpdateName: PropTypes.func.isRequired,
  onSelectShareStatus: PropTypes.func.isRequired,
  onSelectPointPerson: PropTypes.func.isRequired,
  onSubscribe: PropTypes.func.isRequired,
  onUnsubscribe: PropTypes.func.isRequired,
  onUpdateFeePotential: PropTypes.func.isRequired,
  onSwitchStage: PropTypes.func.isRequired,
};

const mapStateToProps = ({ assetNames, currentUser, deal, loading, proposals }) => ({
  assets: assetNames.filter((a) => a.portfolio.id === deal.portfolio.id),
  currentUser,
  deal,
  proposals: orderBy(proposals, (p) => p.createdAt, "desc"),
  isBroker: deal.portfolio.company.id !== currentUser.company.id,
  isCreator: deal.creator.id === currentUser.id,
  isLoading: loading.deal,
});

const mapDispatchToProps = (dispatch, ownProps) => {
  const { dealID } = ownProps.match.params;

  return {
    onUpdateFeePotential: (feePotential) => dispatch(saveDeal({ id: dealID, feePotential })),
    onCancel: () => dispatch(openModal("DealCancelModal", { dealID })),
    onUpdateAssets: (assetIDs) => dispatch(updateAssets(dealID, assetIDs)),
    onUpdateName: (name) => dispatch(saveDeal({ id: dealID, name })),
    onDisregard: (disregardFeedback) => dispatch(disregardDeal(dealID, disregardFeedback)),
    onReopen: () => dispatch(reopenDeal(dealID)),
    onRenew: () => dispatch(renewDeal(dealID)).then((deal) => dispatch(push(`/deals/${deal.id}`))),
    onExecute: (details) => dispatch(executeDeal(dealID, details)),
    onSwitchStage: (stage, assetIds) => dispatch(updateDealStage(dealID, stage, assetIds)),
    onUnsubscribe: () => dispatch(unsubscribeDeal(dealID)),
    onSubscribe: () => dispatch(subscribeDeal(dealID)),
    onSelectPointPerson: (pointPerson) => dispatch(updateDealPointPerson(dealID, pointPerson.id)),
    onSelectShareStatus: (shareStatus, sharedBrokerIDs) =>
      dispatch(updateDealShareStatus(dealID, shareStatus, sharedBrokerIDs)),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(DealHeader));
